import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileText, FlaskConical, FolderOpen, LockKeyhole, Plus } from "lucide-react";
import { SCIENCE_LINKS } from "../../content/siteContent";
import "./ScientistDeployment.css";

export function LabHistory() {
  return (
    <section className="lab-history journey-shell" id="your-lab-data" aria-labelledby="lab-history-title">
      <header className="lab-history-heading">
        <h2 className="journey-title" id="lab-history-title">Your data should work <em>for you.</em></h2>
        <div>
          <p>Years of experiments live in ELNs, instrument files and assay records. Protocols change. Runs fail. The reasons are often buried in someone’s notes.</p>
          <p>We build on-premises systems that help your team use that history. <strong>You approve how your data is used.</strong></p>
        </div>
      </header>

      <figure className="lab-history-boundary" aria-labelledby="lab-history-caption">
        <figcaption id="lab-history-caption"><LockKeyhole size={16} aria-hidden="true" /> On-premises systems · model hosting agreed before deployment</figcaption>
        <div className="lab-history-flow">
          <div className="lab-history-records">
            <h3>Your research history</h3>
            <ul>
              <li><FileText size={18} aria-hidden="true" /> ELNs & protocol versions</li>
              <li><FolderOpen size={18} aria-hidden="true" /> Raw instrument files</li>
              <li><FlaskConical size={18} aria-hidden="true" /> Assay controls & repeats</li>
            </ul>
          </div>
          <ArrowRight className="lab-history-arrow" size={22} aria-hidden="true" />
          <div className="lab-history-agent">
            <img src="/favico.svg" alt="Cornucopia" width="38" height="44" loading="lazy" />
            <h3>A specialized agent</h3>
            <span>Selected for the task</span>
          </div>
          <ArrowRight className="lab-history-arrow" size={22} aria-hidden="true" />
          <div className="lab-history-review">
            <h3>Your scientific judgment</h3>
            <ul><li>Inspect the sources</li><li>Check the analysis</li><li>Decide what to test next</li></ul>
          </div>
        </div>
      </figure>

      <details className="scientist-reasoning-detail">
        <summary>Deployment scope <Plus size={17} aria-hidden="true" /></summary>
        <div>
          <p>Before a pilot, the proposal specifies where models run, which data they receive, where logs are stored, who has access and how long records are retained. Your team approves these decisions, including any external model services or support access, before work begins.</p>
        </div>
      </details>

      <details className="scientist-reasoning-detail">
        <summary>Why start with historical data? <Plus size={17} aria-hidden="true" /></summary>
        <div>
          <p>The MELLODDY study worked with confidential assay data from ten pharmaceutical companies, including discontinued historical assays. Those records contain experimental knowledge that public datasets alone do not capture.</p>
          <p>MELLODDY evaluated federated predictive models, not LLM agents. It supports the value of internal evidence; it does not establish that every historical dataset is useful or that private data always outperform public data. We first check what your records contain and whether they can answer the question.</p>
          <a href="https://pubs.acs.org/doi/10.1021/acs.jcim.3c00799" target="_blank" rel="noreferrer">MELLODDY, JCIM 2024 <ArrowUpRight size={14} aria-hidden="true" /></a>
        </div>
      </details>
    </section>
  );
}

export function ScientificEvaluation() {
  return (
    <section className="scientist-evaluation journey-shell" id="agent-evaluation" aria-labelledby="scientist-evaluation-title">
      <header>
        <h2 className="journey-title" id="scientist-evaluation-title">An agent should be able to tell us <em>we’re wrong.</em></h2>
        <p>We test against evidence, including results that contradict the starting hypothesis. Each pilot begins with agreed criteria and appropriate controls.</p>
      </header>
      <div className="scientist-evaluation-method">
        <div className="scientist-evaluation-prompts" aria-label="Example evaluation design, not an agent result">
          <span>Same evidence. Different framing.</span>
          <blockquote>“What does this experiment show?”</blockquote>
          <blockquote>“This proves our hypothesis. Explain why.”</blockquote>
          <p>The conclusion should follow the evidence in both cases.</p>
        </div>
        <div className="scientist-evaluation-checks">
          <h3>What we look for</h3>
          <ul>
            <li>Claims that can be traced to a source.</li>
            <li>Calculations and uncertainty that hold up.</li>
            <li>Pushback when the premise is wrong.</li>
          </ul>
          <details className="scientist-reasoning-detail">
            <summary>How we test for sycophancy <Plus size={17} aria-hidden="true" /></summary>
            <div>
              <p>Research has shown that user preferences and challenges can steer an assistant away from a correct answer. Sharma et al. studied five assistants available in 2023; their results are not a current-model failure rate or a drug-discovery evaluation.</p>
              <p>Our evaluation design pairs a neutral request with a confident but incorrect assumption, using the same evidence. We agree on held-out cases and compare against a baseline with the same model, tools and budget, documenting the specialization being tested and any differences. We use blinded expert review where feasible and retest when the model, tools or data change. Experimental work also needs appropriate positive and negative controls.</p>
              <p>These tests help detect and reduce sycophancy. They cannot guarantee its absence.</p>
              <a href="https://arxiv.org/html/2310.13548v3" target="_blank" rel="noreferrer">Sharma et al., ICLR 2024 <ArrowUpRight size={14} aria-hidden="true" /></a>
            </div>
          </details>
        </div>
      </div>
      <div className="scientist-evaluation-decision">
        <h3>Before expanding a pilot</h3>
        <ol aria-label="Pilot go or no-go review">
          <li><span>01</span>Agree criteria</li>
          <li><span>02</span>Compare a matched baseline</li>
          <li><span>03</span>Review failures</li>
          <li><span>04</span>Approve the next scope</li>
        </ol>
        <nav aria-label="Evaluation details">
          <Link to={`${SCIENCE_LINKS.solutions.to}#agent-evaluation-harness`}>Evaluation deliverables <ArrowUpRight size={16} aria-hidden="true" /></Link>
          <Link to={SCIENCE_LINKS.evaluation.to}>{SCIENCE_LINKS.evaluation.label} <ArrowUpRight size={16} aria-hidden="true" /></Link>
        </nav>
      </div>
    </section>
  );
}
